import React, { useCallback, useEffect, useState } from 'react';

import TagManager from 'react-gtm-module';

import { LoadingType, PreQualificationStep, setPreQualificationStep } from 'prequalificationV2/actions';
import { setDashboardStep } from 'prequalificationV2/actions/stages';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import * as notificationManager from 'notifications/index';
import { FailureNotificationType } from 'notifications/index';
import { OffersResponseDto } from 'api/AutomatedOffersApi';
import AcceptAutomatedOffer from 'prequalificationV2/pages/PreQualification/steps/AcceptOffer/AutomatedOffers/AcceptAutomatedOffer';
import { DashboardStep } from 'enums/DashboardStep';
import { clearApplication } from 'prequalificationV2/actions/application';
import { GOOGLE_TAG_MANAGER_ARGS } from 'constants/constants';
import { AppRoutes } from 'prequalificationV2/routes/routes.enums';
import useAppNavigate from 'hooks/useAppNavigate';
import { ApplicationStatus } from 'enums/ApplicationStatus';
import { posAutomatedOffersApi } from 'api/SmallLoanProgram/AutomatedOffersApi';
import { OfferDto } from 'api/OffersApi';
import { marketingApi } from 'api/MarketingApi';
import { PrequalificationVersion } from 'enums/ApplicationsVariables';
import { ConceptualHqEvent } from 'enums/ConceptualHqEvent';
import { getConceptualDeviceId } from 'prequalificationV2/routes/routes.utils';
import { useNavigateAfterUnderwriting } from 'prequalificationV2/hooks/useNavigateAfterUnderwriting';

interface AcceptPosAutomatedOfferProps {
  isPrequal: boolean;
}

const AcceptPosAutomatedOffer = ({ isPrequal }: AcceptPosAutomatedOfferProps) => {
  const navigator = useAppNavigate();
  const dispatch = useAppDispatch();
  const { getAndNavigateToNextStep } = useNavigateAfterUnderwriting();
  const isGlobalLoading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const [isLoading, setIsLoading] = useState(true);
  const { applicationId, variables, applicationStatus } = useAppSelector((state) => state.application);
  const { borrowerId } = useAppSelector((state) => state.borrower);
  const [loanOffers, setLoanOffers] = useState<OfferDto[]>([]);
  const [currentLoanOffer, setCurrentLoanOffer] = useState<OfferDto | null>(null);
  const [maxLoanAmount, setMaxLoanAmount] = useState<number>(0);
  const [minLoanAmount, setMinLoanAmount] = useState<number>(0);
  const [loanAmount, setLoanAmount] = useState<number>(variables!.loan_amount!);

  useEffect(() => {
    const track = async () => {
      const deviceId = await getConceptualDeviceId();

      if (!deviceId) {
        return;
      }

      marketingApi
        .trackConceptualHqEvent({
          event: ConceptualHqEvent.Offers,
          prequalificationVersion: PrequalificationVersion.Second,
          borrowerId: borrowerId,
          deviceId: deviceId,
        })
        .catch(() => {});
    };

    track();
  }, []);

  const updateLoanOffers = useCallback((loanOffersResponse: OffersResponseDto) => {
    setLoanOffers(loanOffersResponse.loanOffers);
    setMaxLoanAmount(loanOffersResponse.maxLoanAmount);
    setMinLoanAmount(loanOffersResponse.minLoanAmount);

    if (loanOffersResponse.maxLoanAmount && loanAmount > loanOffersResponse.maxLoanAmount) {
      setLoanAmount(loanOffersResponse.maxLoanAmount);
    }
  }, []);

  const handleApplicationRejected = () => {
    TagManager.dataLayer(GOOGLE_TAG_MANAGER_ARGS.applicationDecline);
    dispatch(setPreQualificationStep(PreQualificationStep.NoOptions));
    navigator(AppRoutes.Prequalification);
  };

  const getLoanOffers = useCallback(async () => {
    try {
      setIsLoading(true);

      const loanOffersResponse = await posAutomatedOffersApi.getOffers({
        applicationId: applicationId!,
        loanAmount: loanAmount,
      });

      if (loanOffersResponse.isApplicationRejected) {
        handleApplicationRejected();
      } else {
        updateLoanOffers(loanOffersResponse);
      }
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  }, [applicationId, dispatch, loanAmount, updateLoanOffers]);

  useEffect(() => {
    if (applicationStatus === ApplicationStatus.Pitched) {
      getLoanOffers();
    }
  }, [applicationStatus, getLoanOffers, loanAmount]);

  const handleDeclineOffer = async () => {
    let isSuccess = false;

    try {
      setIsLoading(true);

      const result = await posAutomatedOffersApi.declineOffer({
        applicationId: applicationId!,
      });

      if (result.isApplicationRejected) {
        dispatch(clearApplication());
      }

      isSuccess = true;
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }

    return isSuccess;
  };

  const handleCloseDeclineOfferThanksPopup = async () => {
    if (applicationId) {
      dispatch(setDashboardStep(DashboardStep.NewInquiry));
    } else {
      dispatch(setDashboardStep(null));
    }

    const isLoggedIn = !!localStorage.getItem('token');

    if (!isLoggedIn) {
      dispatch(setPreQualificationStep(PreQualificationStep.BorrowerIdentity));
      navigator(AppRoutes.Prequalification);

      return;
    }

    await getAndNavigateToNextStep();
  };

  const handleAcceptOffer = async () => {
    try {
      setIsLoading(true);

      const result = await posAutomatedOffersApi.acceptOffer({
        applicationId: applicationId!,
        loanAmount: loanAmount,
        term: currentLoanOffer!.term,
        lender: currentLoanOffer!.lender,
      });

      if (result.isApplicationRejected) {
        handleApplicationRejected();
      } else {
        if (currentLoanOffer!.offerUrl) {
          window.open(currentLoanOffer!.offerUrl, '_self');
        } else {
          dispatch(setDashboardStep(null));
          await getAndNavigateToNextStep();
        }
      }
    } catch (error: any) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AcceptAutomatedOffer
        loanAmount={loanAmount}
        loanOffers={loanOffers}
        setLoanAmount={setLoanAmount}
        minLoanAmount={minLoanAmount}
        maxLoanAmount={maxLoanAmount}
        currentLoanOffer={currentLoanOffer}
        setCurrentLoanOffer={setCurrentLoanOffer}
        onCloseDeclineOfferThanksPopup={handleCloseDeclineOfferThanksPopup}
        onDeclineOffer={handleDeclineOffer}
        onAcceptOffer={handleAcceptOffer}
        isLoading={isLoading || isGlobalLoading}
        isPrequal={isPrequal}
      />
    </>
  );
};

export default AcceptPosAutomatedOffer;
